import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/features/auth/AuthProvider";
import {
  attributeReferral,
  consumePendingReferralCode,
  savePendingReferralCode,
} from "@/lib/referral";
import { Wordmark } from "@/components/editorial";
import { Button } from "@/components/ui/button";
import { getFoodImageUrl } from "@/lib/stock-food-images";
import {
  AISLE_LABELS,
  formatPrice,
  formatWeekRange,
  parseISODate,
} from "@/features/menu/menuLabels";
import { useMenu } from "@/features/menu/store";
import {
  adoptionTargetWeek,
  toAdoptedDraft,
} from "@/features/menu/adoptedPlan";
import { cn } from "@/lib/utils";
import type {
  AdoptMenuResponse,
  Aisle,
  MenuPlan,
  ShoppingList,
  ValidatedMenu,
} from "@/types/menu";
import type { Recipe } from "@/types/menu";

const DAY_LABELS_FR = [
  "Dim",
  "Lun",
  "Mar",
  "Mer",
  "Jeu",
  "Ven",
  "Sam",
] as const;

const DAY_LETTERS = ["D", "L", "M", "M", "J", "V", "S"] as const;

/**
 * Read-only shared menu page — no editing, no swap, no paywall.
 * Route: /share/:token
 */
export function SharedMenuPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { session } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setMenuPlan = useMenu((s) => s.setPlan);
  const [data, setData] = useState<ValidatedMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adopting, setAdopting] = useState(false);
  const [adoptResult, setAdoptResult] = useState<{
    warnings: string[];
    blocked: boolean;
    success: boolean;
    plan?: MenuPlan;
  } | null>(null);

  const refCode = searchParams.get("ref");
  // Prevent double-attribution on re-renders.
  const attributedRef = useRef(false);

  // Persist the referral code before the sign-up redirect, best-effort.
  useEffect(() => {
    if (refCode) {
      savePendingReferralCode(refCode).catch(() => undefined);
    }
  }, [refCode]);

  // Attribute the referral when already logged in (direct flow, not post-redirect).
  // The post-redirect flow is handled by AuthCallback.tsx.
  useEffect(() => {
    if (session && refCode && !attributedRef.current) {
      attributedRef.current = true;
      attributeReferral(refCode)
        .then(() => consumePendingReferralCode())
        .catch(() => undefined);
    }
  }, [session, refCode]);

  useEffect(() => {
    if (!token) {
      setError("Aucun token de partage fourni.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api<ValidatedMenu>(`/api/v1/share/${token}`)
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : "Impossible de charger le menu.",
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleAdopt() {
    if (!token || !session) return;
    setAdopting(true);
    setAdoptResult(null);
    try {
      const res = await api<AdoptMenuResponse>("/api/v1/menu/adopt", {
        method: "POST",
        body: { share_token: token },
      });
      if (res.allergen_warnings.length > 0) {
        setAdoptResult({
          warnings: res.allergen_warnings,
          blocked: true,
          success: false,
        });
      } else {
        // Plan adopted successfully — navigate to menu page with forked plan.
        setAdoptResult({
          warnings: [],
          blocked: false,
          success: true,
          plan: res.plan,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("referral.adopt_error"));
    } finally {
      setAdopting(false);
    }
  }

  // The forked plan used to be passed as router state that no page read,
  // so it was lost. Write it to the menu store as a draft on this user's
  // planning week and open the reveal, where the user curates it and
  // validates explicitly (which persists it and triggers the referral
  // reward).
  function handleOpenAdopted() {
    if (!adoptResult?.plan) return;
    setMenuPlan(toAdoptedDraft(adoptResult.plan, adoptionTargetWeek()));
    navigate("/menu/reveal");
  }

  const plan = data?.plan ?? null;
  // A « liste de courses » share carries no recipes: nothing to adopt.
  const hasMenu = (plan?.days.length ?? 0) > 0;

  return (
    <div className="flex h-full flex-col bg-background pb-2">
      {/* Header */}
      <header className="flex items-center justify-center border-b border-hair px-5 py-4">
        <Wordmark size="sm" />
      </header>

      {loading && (
        <p className="px-6 py-12 text-center text-[13px] text-ink-50">
          Chargement du menu partagé…
        </p>
      )}

      {error && !loading && (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <p className="mb-2 text-[15px] font-medium text-destructive">
            Menu introuvable
          </p>
          <p className="max-w-xs text-[13px] text-ink-50">{error}</p>
          <p className="mt-6 text-[12px] text-ink-30">
            Le lien a peut-être expiré ou a été révoqué.
          </p>
        </div>
      )}

      {plan && <SharedMenuContent plan={plan} />}

      {/* Adopt result feedback */}
      {adoptResult?.blocked && (
        <div className="mx-5 mb-3 rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-[13px] font-medium text-destructive">
            {t("referral.adopt_allergen_title")}
          </p>
          <p className="mt-1 text-[12px] text-destructive/80">
            {t("referral.adopt_allergen_body", {
              warnings: adoptResult.warnings.join(", "),
            })}
          </p>
          <button
            type="button"
            className="mt-2 text-[11px] text-destructive/60 underline underline-offset-2"
            onClick={() => setAdoptResult(null)}
          >
            {t("referral.adopt_allergen_dismiss")}
          </button>
        </div>
      )}
      {adoptResult?.success && (
        <div className="mx-5 mb-3 rounded-sm border border-green-200 bg-green-50 px-4 py-3">
          <p className="mb-2 text-[13px] text-green-800">
            {t("referral.adopt_success")}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={handleOpenAdopted}
          >
            {t("referral.adopt_go_to_menu")}
          </Button>
        </div>
      )}

      {/* Footer CTA */}
      <footer className="border-t border-hair px-5 py-6 text-center">
        {session && plan && hasMenu && !adoptResult?.success && (
          <Button
            className="mb-4 w-full"
            onClick={() => void handleAdopt()}
            disabled={adopting || adoptResult?.blocked}
          >
            {adopting ? t("referral.adopt_loading") : t("referral.adopt_cta")}
          </Button>
        )}
        <p className="mb-2 text-[13px] text-ink-50">
          Planifié avec{" "}
          <Link
            to="/"
            className="font-serif italic text-accent hover:underline"
          >
            Batch Cooking
          </Link>
        </p>
        <p className="text-[11px] text-ink-30">
          Téléchargez l'app pour créer votre propre menu personnalisé.
        </p>
      </footer>
    </div>
  );
}

// ─── Shared content — read-only ──────────────────────────────

function SharedMenuContent({ plan }: { plan: MenuPlan }) {
  const { t } = useTranslation();
  const hasMenu = plan.days.length > 0;
  const totalKcal = plan.days.reduce((sum, d) => {
    const meals = Object.values(d.meals).filter(Boolean) as Recipe[];
    return sum + meals.reduce((mealSum, r) => mealSum + (r.calories ?? 0), 0);
  }, 0);

  return (
    <>
      {/* Hero */}
      <section className="border-b border-hair px-6 py-6">
        {plan.week_start && (
          <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-ink-50">
            {`Semaine ${weekNumber(plan.week_start)} · ${formatWeekRange(
              plan.week_start,
              plan.days.length,
            )}`}
          </p>
        )}
        <h1 className="h-display text-[40px] leading-[1] sm:text-[44px]">
          <span className="italic">
            {hasMenu ? t("share.menu_title") : t("share.shopping_list_title")}
          </span>
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[12px] text-ink-50">
          {hasMenu && <span>{totalKcal.toLocaleString("fr-FR")} kcal</span>}
          {plan.estimated_cost_eur != null && (
            <span>{formatPrice(plan.estimated_cost_eur)} estimés</span>
          )}
        </div>
      </section>

      {/* Day strip */}
      <div className="flex gap-2 overflow-x-auto border-b border-hair px-6 py-4">
        {plan.days.map((d, i) => {
          const date = parseISODate(d.day);
          const letter = DAY_LETTERS[date.getDay()];
          const num = date.getDate();
          return (
            <div
              key={d.day}
              className={cn(
                "flex w-12 shrink-0 flex-col items-center rounded-pill border px-2 py-2",
                i === 0
                  ? "border-foreground bg-foreground text-background"
                  : "border-hair bg-background text-foreground",
              )}
            >
              <span className="text-[9px] uppercase tracking-[0.2em] opacity-70">
                {letter}
              </span>
              <span className="font-serif text-[16px]">{num}</span>
            </div>
          );
        })}
      </div>

      {/* Meal cards — read-only, no swap/fav */}
      <div className="flex-1 overflow-auto px-5 py-4">
        <ul className="grid gap-3">
          {plan.days.map((d) => {
            const recipe = d.meals.dinner;
            if (!recipe) return null;
            return (
              <li key={d.day}>
                <SharedMealCard date={d.day} recipe={recipe} />
              </li>
            );
          })}
        </ul>
        {plan.shopping_list && <SharedShoppingList list={plan.shopping_list} />}
      </div>
    </>
  );
}

function SharedShoppingList({ list }: { list: ShoppingList }) {
  const { t } = useTranslation();
  const aisles = (Object.keys(list.items_by_aisle) as Aisle[]).sort(
    (a, b) => (AISLE_LABELS[a]?.order ?? 999) - (AISLE_LABELS[b]?.order ?? 999),
  );
  return (
    <section aria-label={t("share.shopping_list_heading")} className="mt-6">
      <h2 className="mb-3 text-[10px] uppercase tracking-[0.2em] text-ink-50">
        {t("share.shopping_list_heading")}
      </h2>
      {aisles.map((aisle) => (
        <div key={aisle} className="mb-4">
          <h3 className="mb-1 font-serif text-[15px]">
            {(AISLE_LABELS[aisle] ?? AISLE_LABELS.other).fr}
          </h3>
          <ul className="grid gap-1 text-[13px]">
            {(list.items_by_aisle[aisle] ?? []).map((item) => (
              <li
                key={`${aisle}-${item.name}`}
                className="flex justify-between border-b border-hair py-1"
              >
                <span>{item.name}</span>
                <span className="font-mono text-[11px] text-ink-50">
                  {item.quantity} {item.unit}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function SharedMealCard({ date, recipe }: { date: string; recipe: Recipe }) {
  const d = parseISODate(date);
  const totalMin = recipe.prep_minutes + recipe.cook_minutes;
  return (
    <article className="flex items-stretch gap-3.5 border border-hair bg-background p-3">
      <div className="flex w-14 flex-col items-center justify-center border-r border-hair pr-3 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-50">
          {DAY_LABELS_FR[d.getDay()]}
        </div>
        <div className="font-serif text-2xl font-normal tracking-[-0.01em]">
          {d.getDate()}
        </div>
      </div>
      <div className="h-[70px] w-[70px] shrink-0 overflow-hidden rounded-sm bg-hair">
        {/* TODO(#menu-images): Replace stock photo with real menu item image
            when menu-specific image upload is implemented. */}
        <img
          src={getFoodImageUrl(recipe.slug)}
          alt={recipe.title}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[9.5px] uppercase tracking-[0.18em] text-ink-50">
          {recipe.nutri_score ? `Nutri ${recipe.nutri_score}` : "—"}
        </div>
        <div className="font-serif text-[16px] leading-[1.15]">
          {recipe.title}
        </div>
        <div className="mt-1.5 flex gap-2 font-mono text-[11px] text-ink-50">
          <span>{totalMin}'</span>
          <span>·</span>
          <span>{recipe.servings} pers.</span>
        </div>
      </div>
    </article>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function weekNumber(iso: string): number {
  const d = parseISODate(iso);
  const onejan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(
    ((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
  );
}
